import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/hooks/useSEO";
import JsonLd from "@/components/JsonLd";
import { POSTS } from "@/data/blog";
import { ArrowRight, Clock } from "lucide-react";

export default function Blog() {
  useSEO({
    title: "Safety Resources & Tips for Parents and Educators",
    description: "Practical fire safety, seat belt, and crosswalk safety tips for kids — plus guidance for parents, schools and daycares from The Safety Quest.",
  });

  const base = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <>
      <JsonLd id="blog-list" data={{
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "The Safety Quest Resources",
        url: `${base}/blog`,
        blogPost: POSTS.map((p) => ({
          "@type": "BlogPosting",
          headline: p.title,
          datePublished: p.date,
          author: { "@type": "Person", name: p.author },
          url: `${base}/blog/${p.slug}`,
        })),
      }} />

      <section className="pt-36 pb-16 max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mb-4">Safety Resources</p>
          <h1 className="font-display text-6xl sm:text-8xl tracking-tight leading-[0.85] max-w-4xl">
            Tips, guides & safety know-how.
          </h1>
          <p className="font-body text-lg text-sq-ink/70 mt-6 max-w-2xl">
            Practical, expert-backed advice on keeping kids safe — from fire drills and seat belts to
            crosswalks and choosing the right safety books. Written for parents, teachers and daycares.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {POSTS.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.08}>
              <Link to={`/blog/${p.slug}`} data-testid={`post-card-${p.slug}`} className="group flex flex-col h-full rounded-3xl bg-white border-2 border-sq-ink overflow-hidden shadow-[0_6px_0_#111827] hover:-translate-y-1 transition-transform">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-xs text-sq-ink/50 font-semibold">
                    <span>{new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {p.readTime}</span>
                  </div>
                  <h2 className="font-round text-xl font-semibold mt-2 leading-tight">{p.title}</h2>
                  <p className="text-sq-ink/60 text-sm mt-2 flex-1">{p.excerpt}</p>
                  <span className="inline-flex items-center gap-2 mt-4 font-bold text-sq-red group-hover:gap-3 transition-all">Read more <ArrowRight size={16} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
